import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogOut, Plus, Fuel, Clock, Clipboard, Check, Pencil, Copy, Trash2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fiscalApi() {
  const token = localStorage.getItem('fiscal_token');
  return axios.create({
    baseURL: `${API}/api`,
    headers: { Authorization: `Bearer ${token}` }
  });
}

export default function FiscalDashboardPage() {
  const navigate = useNavigate();
  const [fiscal, setFiscal] = useState(null);
  const [lancamentos, setLancamentos] = useState([]);
  const [refData, setRefData] = useState({ secretarias: [], combustiveis: [], postos: [], posto_combustiveis: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    secretaria_nome: '', motorista: '', veiculo: '',
    combustivel_tipo: '', litros: '', posto_nome: '',
    data_hora: new Date().toISOString().slice(0, 16)
  });

  useEffect(() => {
    const token = localStorage.getItem('fiscal_token');
    const data = localStorage.getItem('fiscal_data');
    if (!token || !data) {
      navigate('/fiscal-login');
      return;
    }
    setFiscal(JSON.parse(data));
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const api = fiscalApi();
      const [lancRes, refRes] = await Promise.all([
        api.get('/fiscal/lancamentos'),
        api.get('/fiscal/dados-referencia')
      ]);
      setLancamentos(lancRes.data || []);
      setRefData(refRes.data || { secretarias: [], combustiveis: [], postos: [], posto_combustiveis: {} });
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('fiscal_token');
        localStorage.removeItem('fiscal_data');
        navigate('/fiscal-login');
      }
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { if (fiscal) fetchData(); }, [fiscal, fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.motorista.trim()) return toast.error('Informe o nome do motorista');
    if (!form.combustivel_tipo) return toast.error('Selecione o combustivel');
    if (!form.litros || parseFloat(form.litros) <= 0) return toast.error('Informe a quantidade de litros');
    setSaving(true);
    try {
      const payload = { ...form, litros: parseFloat(form.litros) };
      if (editingId) {
        await fiscalApi().put(`/fiscal/lancamentos/${editingId}`, payload);
        toast.success('Lancamento atualizado!');
        setEditingId(null);
      } else {
        await fiscalApi().post('/fiscal/lancamentos', payload);
        toast.success('Lancamento registrado!');
      }
      setForm({
        secretaria_nome: '', motorista: '', veiculo: '',
        combustivel_tipo: '', litros: '', posto_nome: '',
        data_hora: new Date().toISOString().slice(0, 16)
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    }
    setSaving(false);
  };

  const handleEdit = (l) => {
    setEditingId(l.id);
    setForm({
      secretaria_nome: l.secretaria_nome || '',
      motorista: l.motorista || '',
      veiculo: l.veiculo || '',
      combustivel_tipo: l.combustivel_tipo || '',
      litros: l.litros || '',
      posto_nome: l.posto_nome || '',
      data_hora: l.data_hora ? l.data_hora.slice(0, 16) : new Date().toISOString().slice(0, 16)
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDuplicate = (l) => {
    setEditingId(null);
    setForm({
      secretaria_nome: l.secretaria_nome || '',
      motorista: l.motorista || '',
      veiculo: l.veiculo || '',
      combustivel_tipo: l.combustivel_tipo || '',
      litros: l.litros || '',
      posto_nome: l.posto_nome || '',
      data_hora: new Date().toISOString().slice(0, 16)
    });
    toast.info('Dados copiados. Ajuste e salve.');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (lancId) => {
    if (!window.confirm('Excluir este lancamento?')) return;
    try {
      await fiscalApi().delete(`/fiscal/lancamentos/${lancId}`);
      toast.success('Lancamento excluido');
      fetchData();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm({
      secretaria_nome: '', motorista: '', veiculo: '',
      combustivel_tipo: '', litros: '', posto_nome: '',
      data_hora: new Date().toISOString().slice(0, 16)
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('fiscal_token');
    localStorage.removeItem('fiscal_data');
    navigate('/fiscal-login');
  };

  const fmt = (v) => parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const fmtDate = (d) => {
    try {
      const dt = new Date(d);
      return dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return d; }
  };

  if (!fiscal) return null;

  const totalLitros = lancamentos.reduce((s, l) => s + (l.litros || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="fiscal-dashboard">
      {/* Header */}
      <div className="bg-[#003366] text-white px-4 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <Clipboard size={16} />
          </div>
          <div>
            <h1 className="text-sm font-bold uppercase">Fiscal de Abastecimento</h1>
            <p className="text-[10px] text-blue-200">{fiscal.name}</p>
          </div>
        </div>
        <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={handleLogout} data-testid="fiscal-logout-btn">
          <LogOut size={16} className="mr-1" /> SAIR
        </Button>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-blue-600 font-medium uppercase">Lancamentos</p>
              <p className="text-2xl font-bold text-blue-700" data-testid="fiscal-total-lancamentos">{lancamentos.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-emerald-600 font-medium uppercase">Total Litros</p>
              <p className="text-2xl font-bold text-emerald-700" data-testid="fiscal-total-litros">{fmt(totalLitros)} L</p>
            </CardContent>
          </Card>
        </div>

        {/* Form */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-[#003366] flex items-center gap-2">
              <Plus size={16} /> {editingId ? 'EDITAR LANCAMENTO' : 'NOVO LANCAMENTO'}
            </CardTitle>
            {editingId && (
              <Button size="sm" variant="ghost" className="text-xs text-gray-500" onClick={handleCancelEdit}>
                <X size={14} className="mr-1" /> CANCELAR EDICAO
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="fiscal-lancamento-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Secretaria</Label>
                  <select value={form.secretaria_nome} onChange={e => setForm(p => ({ ...p, secretaria_nome: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border text-sm" data-testid="fiscal-secretaria-select">
                    <option value="">Selecione...</option>
                    {refData.secretarias.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(s => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Motorista *</Label>
                  <Input value={form.motorista} onChange={e => setForm(p => ({ ...p, motorista: e.target.value }))}
                    placeholder="Nome do motorista" required data-testid="fiscal-motorista-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Veiculo (placa ou descricao)</Label>
                  <Input value={form.veiculo} onChange={e => setForm(p => ({ ...p, veiculo: e.target.value }))}
                    placeholder="Ex: ABC-1234 ou Hilux Branca" data-testid="fiscal-veiculo-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Posto *</Label>
                  <select value={form.posto_nome} onChange={e => {
                    const newPosto = e.target.value;
                    setForm(p => ({ ...p, posto_nome: newPosto, combustivel_tipo: '' }));
                  }}
                    className="w-full h-10 px-3 rounded-md border text-sm" data-testid="fiscal-posto-select">
                    <option value="">Selecione o posto...</option>
                    {refData.postos.map(p => (
                      <option key={p.id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Tipo de Combustivel *{!form.posto_nome && <span className="text-gray-400 ml-1">(selecione o posto)</span>}</Label>
                  <select value={form.combustivel_tipo} onChange={e => setForm(p => ({ ...p, combustivel_tipo: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border text-sm" required disabled={!form.posto_nome} data-testid="fiscal-combustivel-select">
                    <option value="">{form.posto_nome ? 'Selecione...' : 'Selecione o posto primeiro'}</option>
                    {(() => {
                      const selectedPosto = refData.postos.find(p => p.name === form.posto_nome);
                      const combIds = selectedPosto ? (refData.posto_combustiveis[selectedPosto.id] || []) : [];
                      const filtered = combIds.length > 0
                        ? refData.combustiveis.filter(c => combIds.includes(c.id))
                        : [];
                      return filtered.map(c => (
                        <option key={c.id} value={c.type}>{c.type}</option>
                      ));
                    })()}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Quantidade (Litros) *</Label>
                  <Input type="number" step="0.1" min="0.1" value={form.litros} onChange={e => setForm(p => ({ ...p, litros: e.target.value }))}
                    placeholder="Ex: 50" required data-testid="fiscal-litros-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Data e Hora</Label>
                  <Input type="datetime-local" value={form.data_hora} onChange={e => setForm(p => ({ ...p, data_hora: e.target.value }))}
                    data-testid="fiscal-datahora-input" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#003366] hover:bg-[#1E3A5F]" disabled={saving} data-testid="fiscal-submit-btn">
                <Check size={16} className="mr-2" /> {saving ? 'SALVANDO...' : editingId ? 'ATUALIZAR LANCAMENTO' : 'REGISTRAR LANCAMENTO'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* My Records */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-[#003366] flex items-center gap-2">
              <Clock size={16} /> MEUS LANCAMENTOS ({lancamentos.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-4"><div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full" /></div>
            ) : lancamentos.length === 0 ? (
              <p className="text-center text-gray-400 py-6 text-sm">Nenhum lancamento registrado</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="fiscal-lancamentos-table">
                  <thead>
                    <tr className="bg-[#003366] text-white">
                      <th className="px-2 py-1.5 text-left rounded-tl-lg">Data/Hora</th>
                      <th className="px-2 py-1.5 text-left">Motorista</th>
                      <th className="px-2 py-1.5 text-left">Veiculo</th>
                      <th className="px-2 py-1.5 text-left">Secretaria</th>
                      <th className="px-2 py-1.5 text-left">Combustivel</th>
                      <th className="px-2 py-1.5 text-right">Litros</th>
                      <th className="px-2 py-1.5 text-left">Posto</th>
                      <th className="px-2 py-1.5 text-center rounded-tr-lg">Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lancamentos.map((l, idx) => (
                      <tr key={l.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-2 py-1.5">{fmtDate(l.data_hora)}</td>
                        <td className="px-2 py-1.5 uppercase font-medium">{l.motorista}</td>
                        <td className="px-2 py-1.5 uppercase">{l.veiculo || '-'}</td>
                        <td className="px-2 py-1.5 uppercase">{l.secretaria_nome || '-'}</td>
                        <td className="px-2 py-1.5">{l.combustivel_tipo}</td>
                        <td className="px-2 py-1.5 text-right font-bold text-emerald-600">{fmt(l.litros)} L</td>
                        <td className="px-2 py-1.5">{l.posto_nome || '-'}</td>
                        <td className="px-2 py-1.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => handleEdit(l)} className="p-1 hover:bg-blue-50 rounded text-blue-500" title="Editar" data-testid={`edit-lanc-${l.id}`}>
                              <Pencil size={13} />
                            </button>
                            <button onClick={() => handleDuplicate(l)} className="p-1 hover:bg-green-50 rounded text-green-500" title="Duplicar" data-testid={`dup-lanc-${l.id}`}>
                              <Copy size={13} />
                            </button>
                            <button onClick={() => handleDelete(l.id)} className="p-1 hover:bg-red-50 rounded text-red-400" title="Excluir" data-testid={`del-lanc-${l.id}`}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
